import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { MessageSquare, Eye, EyeOff, Moon, Sun } from "lucide-react";
import "../global.css";
import { useAuth } from "../contexts/AuthContext";
import ChangeTempPasswordModal from "../components/ChangeTempPasswordModal";

const Login = () => {
  const { darkMode, setDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    email: "",
    senha: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const { login, user, finalizarLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) { navigate("/"); return; }
    const params = new URLSearchParams(window.location.search);
    if (params.get('email_confirmed') === 'true') toast.success('Email confirmado com sucesso! Faça login.');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData);
      if (result.success) {
        // Verificar se tem senha temporária
        if (result.data?.senha_temporaria) {
          setShowTempPasswordModal(true);
          toast.info('Por segurança, altere sua senha temporária');
        } else {
          toast.success("Login realizado com sucesso!");
          navigate("/");
        }
      } else if (result.suspended) {
        navigate("/conta-suspensa", { state: { cliente_id: result.cliente_id, motivo: result.motivo } });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleTempPasswordChanged = async () => {
    setShowTempPasswordModal(false);
    await finalizarLogin();
    toast.success("Login realizado com sucesso!");
    navigate("/");
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8  ${
        darkMode ? "content-bg-dark" : "bg-gray-100"
      }`}
    >
      <button
        onClick={() => setDarkMode(!darkMode)}
        className={`fixed top-4 right-4 p-2 rounded-full ${
          darkMode
            ? "bg-gray-800 text-gray-200 hover:text-white hover:bg-gray-700"
            : "bg-white text-gray-400 hover:text-gray-500"
        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
      >
        {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center">
            <img
              src="/assets/logo_nomoz.png"
              width={177}
              height={65}
              alt="Nomoz Logo"
            />
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="input-wrapper">
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                  darkMode
                    ? "bg-zinc-600 border border-gray-700 text-white focus:ring-green-500 focus:border-green-500"
                    : "bg-white border border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500"
                }`}
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div className="input-wrapper relative">
              <label
                className={`block text-sm font-medium mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Senha
              </label>
              <input
                id="senha"
                name="senha"
                type={showPassword ? "text" : "password"}
                required
                className={`block w-full rounded-md shadow-sm py-2 px-3 ${
                  darkMode
                    ? "bg-zinc-600 border border-gray-700 text-white focus:ring-green-500 focus:border-green-500"
                    : "bg-white border border-gray-300 text-gray-900 focus:ring-green-500 focus:border-green-500"
                }`}
                placeholder="Senha"
                value={formData.senha}
                onChange={handleChange}
              />
              <button
                type="button"
                className="mt-7 absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="gradient-button group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                "Entrar"
              )}
            </button>
          </div>

          <div className="text-center">
            <a
              href="/forgot-password"
              className={`text-sm ${darkMode ? 'text-nomoz-orange hover:text-orange-400' : 'text-nomoz-orange hover:text-orange-600'}`}
            >
              Esqueci minha senha
            </a>
          </div>

          <div className="text-center">
            <Link
              to="/register"
              className={`text-sm ${darkMode ? 'text-nomoz-orange hover:text-orange-400' : 'text-nomoz-orange hover:text-orange-600'}`}
            >
              Não tem conta? Crie grátis
            </Link>
          </div>
        </form>
      </div>

      {showTempPasswordModal && (
        <ChangeTempPasswordModal onSuccess={handleTempPasswordChanged} />
      )}
    </div>
  );
};

export default Login;
